import React from 'react';
import { ArrowRight, ArrowLeft, Star, Eye, Heart, Zap, MessageCircle, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CookieBanner from '../components/layout/CookieBanner';

const values = [
  {
    icon: Star,
    title: 'Excellence',
    description: 'We deliver what we promise and go the extra mile. Often, we demand more of ourselves than our clients do.'
  },
  {
    icon: Eye,
    title: 'Transparency',
    description: 'We are fully invested in your success and we will always be transparent in our approach.'
  },
  {
    icon: Heart,
    title: 'Passion',
    description: 'We absolutely love what we do. Seeing our partners succeed is why we do what we do and why we work so passionately.'
  },
  {
    icon: Zap,
    title: 'Innovation',
    description: "Technology is always changing. That's why our approach is always fluid and mobile."
  },
  {
    icon: MessageCircle,
    title: 'Communication',
    description: 'As an extension of your team, we will always be accessible for you. That means, day or night 7 days a week.'
  },
  {
    icon: Target,
    title: 'Commitment',
    description: 'Your success is our success. We are and will always be fully committed to helping you reach your growth goals.'
  }
];

const teamPhotos = [
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/0bmz3ii4_151321341_1105461213294333_1714450491378723923_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/fi7uucra_120758701_1005800656593723_8601242139969842905_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/rmo5geri_273858305_1359976641176121_3539699508377132724_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/hnw2f1v9_289644348_1451681025339015_4097531883846428535_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/ds4r0bq4_330161188_1545216532644460_316146939038201560_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/xt7omjsm_320384140_3392794890958188_3046960616185618437_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/30qft3b2_337172242_157682883868890_5809046002561639023_n-1920w.webp',
  'https://customer-assets.emergentagent.com/job_modern-gomobyle/artifacts/prezs72l_332858850_506106368363538_5168879283758219782_n-1920w.webp'
];

const About = () => {
  return (
    <div className="min-h-screen bg-[#f8f7f4]">
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex flex-col justify-center overflow-hidden bg-[#f8f7f4] pt-32 pb-16">
          {/* Gradient blob background */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-0 right-0 w-[80%] h-[120%] opacity-50"
              style={{
                background: `
                  radial-gradient(ellipse 70% 50% at 75% 20%, rgba(255, 180, 120, 0.3) 0%, transparent 50%),
                  radial-gradient(ellipse 60% 50% at 85% 50%, rgba(200, 150, 255, 0.25) 0%, transparent 50%),
                  radial-gradient(ellipse 50% 60% at 65% 70%, rgba(150, 200, 255, 0.2) 0%, transparent 50%)
                `,
                filter: 'blur(60px)',
                transform: 'rotate(-10deg)'
              }}
            />
          </div>

          <div className="relative z-10 px-6 md:px-12 lg:px-24">
            {/* Back link */}
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-gray-600 hover:text-black transition-colors mb-12"
            >
              <ArrowLeft size={16} /> Back to Home
            </Link>

            <div className="max-w-4xl">
              <span className="text-sm uppercase tracking-widest text-gray-500 mb-4 block">About Us</span>
              <h1 className="text-[clamp(2.5rem,6vw,5rem)] font-light leading-[1.1] text-black mb-8">
                Every project has its own{' '}
                <span className="font-medium">personality.</span>
              </h1>
              <p className="text-xl text-gray-700 max-w-2xl">
                We approach each challenge with fresh energy and inspiration, so you get the best results.
              </p>
            </div>
          </div>
        </section>

        {/* Photo Gallery - Top Row */}
        <section className="py-12 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={teamPhotos[0]} 
                  alt="Team at work"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={teamPhotos[1]} 
                  alt="Creative collaboration"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3] hidden md:block">
                <img 
                  src={teamPhotos[2]} 
                  alt="Team event"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-24 bg-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="mb-16">
              <h2 className="text-[clamp(3rem,8vw,8rem)] font-light text-gray-300 leading-none tracking-tight">
                <span className="block">OUR</span>
                <span className="block ml-[10%]">VALUES</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div key={index} className="group">
                  <div className="bg-gray-50 rounded-2xl p-8 h-full hover:bg-gray-100 transition-colors">
                    <value.icon className="w-10 h-10 text-gray-400 group-hover:text-black mb-6 transition-colors" />
                    <h3 className="text-2xl font-medium text-black mb-4">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Photo Gallery - Bottom Row */}
        <section className="py-12 bg-[#f8f7f4]">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={teamPhotos[3]} 
                  alt="Behind the scenes"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={teamPhotos[4]} 
                  alt="Team celebration"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={teamPhotos[5]} 
                  alt="Team working"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="group relative overflow-hidden rounded-2xl aspect-[4/3]">
                <img 
                  src={teamPhotos[6]} 
                  alt="Creative session"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mt-4">
              <div className="group relative overflow-hidden rounded-2xl aspect-[21/9]">
                <img 
                  src={teamPhotos[7]} 
                  alt="Team photo"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-[#0a0a0a] text-white">
          <div className="px-6 md:px-12 lg:px-24">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-4xl md:text-5xl font-light mb-6">
                Ready to work with a team that{' '}
                <span className="font-medium">truly cares?</span>
              </h2>
              <p className="text-gray-400 text-lg mb-8">
                Let's create something amazing together. We're excited to hear about your project.
              </p>
              <a
                href="mailto:hello@gomobyle.com"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-black rounded-full font-medium hover:bg-gray-200 transition-colors"
              >
                Get in Touch <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default About;
